// Gemeinsame Spiellogik – wird von Server UND Browser genutzt.
// Alle Zahlen sind eigene Werte und bewusst leicht anpassbar (Balancing).

export const WORLD = {
  speed: 1, // globaler Geschwindigkeitsfaktor (Produktion + Bauzeiten)
  mapSize: 200, // Karte geht von 0..mapSize-1 in x und y
  maxQueue: 2, // gleichzeitige Bauaufträge pro Dorf
  startResources: 500,
};

export type Resource = "holz" | "lehm" | "eisen";
export const RESOURCES: Resource[] = ["holz", "lehm", "eisen"];
export type Cost = Record<Resource, number>;

export type BuildingId =
  | "hauptgebaeude"
  | "holzfaeller"
  | "lehmgrube"
  | "eisenmine"
  | "speicher"
  | "bauernhof"
  | "kaserne"
  | "wall"
  | "adelshof";

export interface BuildingDef {
  name: string;
  description: string;
  maxLevel: number;
  baseCost: Cost; // Kosten für Stufe 1
  costFactor: number; // Kostenwachstum je Stufe
  baseTime: number; // Bauzeit Stufe 1 in Sekunden (vor Hauptgebäude-Bonus)
  timeFactor: number;
  pop: number; // Bevölkerung, die Stufe 1 belegt
  popFactor: number;
  requires?: Partial<Record<BuildingId, number>>;
}

export const BUILDINGS: Record<BuildingId, BuildingDef> = {
  hauptgebaeude: {
    name: "Hauptgebäude",
    description: "Verkürzt die Bauzeit aller Gebäude.",
    maxLevel: 20,
    baseCost: { holz: 90, lehm: 80, eisen: 70 },
    costFactor: 1.26,
    baseTime: 60,
    timeFactor: 1.2,
    pop: 5,
    popFactor: 1.17,
  },
  holzfaeller: {
    name: "Holzfäller",
    description: "Produziert Holz.",
    maxLevel: 30,
    baseCost: { holz: 50, lehm: 60, eisen: 40 },
    costFactor: 1.25,
    baseTime: 30,
    timeFactor: 1.2,
    pop: 5,
    popFactor: 1.155,
  },
  lehmgrube: {
    name: "Lehmgrube",
    description: "Produziert Lehm.",
    maxLevel: 30,
    baseCost: { holz: 65, lehm: 50, eisen: 40 },
    costFactor: 1.27,
    baseTime: 30,
    timeFactor: 1.2,
    pop: 10,
    popFactor: 1.14,
  },
  eisenmine: {
    name: "Eisenmine",
    description: "Produziert Eisen.",
    maxLevel: 30,
    baseCost: { holz: 75, lehm: 65, eisen: 70 },
    costFactor: 1.252,
    baseTime: 36,
    timeFactor: 1.2,
    pop: 10,
    popFactor: 1.17,
  },
  speicher: {
    name: "Speicher",
    description: "Erhöht die Lagerkapazität für jeden Rohstoff.",
    maxLevel: 30,
    baseCost: { holz: 60, lehm: 50, eisen: 40 },
    costFactor: 1.265,
    baseTime: 34,
    timeFactor: 1.2,
    pop: 0,
    popFactor: 1,
  },
  bauernhof: {
    name: "Bauernhof",
    description: "Erhöht die maximale Bevölkerung.",
    maxLevel: 30,
    baseCost: { holz: 45, lehm: 40, eisen: 30 },
    costFactor: 1.3,
    baseTime: 40,
    timeFactor: 1.2,
    pop: 0,
    popFactor: 1,
  },
  kaserne: {
    name: "Kaserne",
    description: "Bildet Truppen aus. Höhere Stufen schalten Einheiten frei und bilden schneller aus.",
    maxLevel: 25,
    baseCost: { holz: 200, lehm: 170, eisen: 90 },
    costFactor: 1.26,
    baseTime: 120,
    timeFactor: 1.2,
    pop: 7,
    popFactor: 1.17,
    requires: { hauptgebaeude: 3 },
  },
  wall: {
    name: "Wall",
    description: "Stärkt die Verteidigung des Dorfes.",
    maxLevel: 20,
    baseCost: { holz: 50, lehm: 100, eisen: 20 },
    costFactor: 1.26,
    baseTime: 60,
    timeFactor: 1.2,
    pop: 5,
    popFactor: 1.17,
    requires: { kaserne: 1 },
  },
  adelshof: {
    name: "Adelshof",
    description: "Bildet Fürsten aus, die fremde Dörfer übernehmen können.",
    maxLevel: 1,
    baseCost: { holz: 15000, lehm: 25000, eisen: 10000 },
    costFactor: 1,
    baseTime: 3600,
    timeFactor: 1,
    pop: 80,
    popFactor: 1,
    requires: { hauptgebaeude: 10, kaserne: 5 },
  },
};

export const BUILDING_IDS = Object.keys(BUILDINGS) as BuildingId[];
export type Levels = Record<BuildingId, number>;

export function startLevels(): Levels {
  return {
    hauptgebaeude: 1,
    holzfaeller: 1,
    lehmgrube: 1,
    eisenmine: 1,
    speicher: 1,
    bauernhof: 1,
    kaserne: 0,
    wall: 0,
    adelshof: 0,
  };
}

/** Kosten, um ein Gebäude auf `level` auszubauen. */
export function buildCost(id: BuildingId, level: number): Cost {
  const d = BUILDINGS[id];
  const f = Math.pow(d.costFactor, level - 1);
  return {
    holz: Math.round(d.baseCost.holz * f),
    lehm: Math.round(d.baseCost.lehm * f),
    eisen: Math.round(d.baseCost.eisen * f),
  };
}

/** Bauzeit in Sekunden für Ausbau auf `level`. Jede Hauptgebäude-Stufe spart ~5 %. */
export function buildTime(id: BuildingId, level: number, hqLevel: number): number {
  const d = BUILDINGS[id];
  const raw = d.baseTime * Math.pow(d.timeFactor, level - 1);
  const hqBonus = Math.pow(0.95, Math.max(0, hqLevel - 1));
  return Math.max(1, Math.round((raw * hqBonus) / WORLD.speed));
}

/** Rohstoffe pro Stunde bei Minen-Stufe `level`. */
export function productionPerHour(level: number): number {
  if (level <= 0) return 5 * WORLD.speed;
  return Math.round(30 * Math.pow(1.163, level - 1) * WORLD.speed);
}

export function storageCapacity(speicherLevel: number): number {
  return Math.round(1000 * Math.pow(1.23, Math.max(0, speicherLevel - 1)));
}

export function farmCapacity(bauernhofLevel: number): number {
  return Math.round(240 * Math.pow(1.172, Math.max(0, bauernhofLevel - 1)));
}

/** Bevölkerung, die ein Gebäude auf `level` insgesamt belegt. */
export function buildingPop(id: BuildingId, level: number): number {
  const d = BUILDINGS[id];
  if (level <= 0 || d.pop === 0) return 0;
  return Math.round(d.pop * Math.pow(d.popFactor, level - 1));
}

export function popUsed(levels: Levels): number {
  return BUILDING_IDS.reduce((sum, id) => sum + buildingPop(id, levels[id]), 0);
}

export const MINE_FOR: Record<Resource, BuildingId> = {
  holz: "holzfaeller",
  lehm: "lehmgrube",
  eisen: "eisenmine",
};

export function rates(levels: Levels): Cost {
  return {
    holz: productionPerHour(levels.holzfaeller),
    lehm: productionPerHour(levels.lehmgrube),
    eisen: productionPerHour(levels.eisenmine),
  };
}

/** Rohstoffstand zu `atMs`, ausgehend von gespeichertem Stand zu `sinceMs`. */
export function resourcesAt(stored: Cost, sinceMs: number, atMs: number, levels: Levels): Cost {
  const hours = Math.max(0, atMs - sinceMs) / 3_600_000;
  const cap = storageCapacity(levels.speicher);
  const r = rates(levels);
  const out = {} as Cost;
  for (const res of RESOURCES) {
    // Wer über dem Limit ist (z. B. nach Plünderung später), verliert nichts – wächst aber nicht weiter.
    out[res] = stored[res] >= cap ? stored[res] : Math.min(cap, stored[res] + r[res] * hours);
  }
  return out;
}

export function canAfford(have: Cost, cost: Cost): boolean {
  return RESOURCES.every((r) => have[r] >= cost[r]);
}

/**
 * Prüft, ob ein Ausbau erlaubt ist. `planned` = Stufen inkl. bereits laufender Aufträge.
 * Gibt `null` zurück, wenn alles passt, sonst eine Fehlermeldung.
 */
export function buildBlocker(id: BuildingId, planned: Levels, unitPop = 0): string | null {
  const d = BUILDINGS[id];
  const target = planned[id] + 1;
  if (target > d.maxLevel) return "Maximale Stufe erreicht";
  for (const [req, lvl] of Object.entries(d.requires ?? {}) as [BuildingId, number][]) {
    if (planned[req] < lvl) return `Benötigt ${BUILDINGS[req].name} Stufe ${lvl}`;
  }
  const extraPop = buildingPop(id, target) - buildingPop(id, target - 1);
  if (id !== "bauernhof" && popUsed(planned) + unitPop + extraPop > farmCapacity(planned.bauernhof)) {
    return "Bauernhof zu klein";
  }
  return null;
}

// ---------- Punkte ----------

/** Punkte = investierte Rohstoffe / 100 (mindestens 1 je Stufe). */
export function buildingPoints(id: BuildingId, level: number): number {
  let pts = 0;
  for (let l = 1; l <= level; l++) {
    const c = buildCost(id, l);
    pts += Math.max(1, Math.round((c.holz + c.lehm + c.eisen) / 100));
  }
  return pts;
}

export function villagePoints(levels: Levels): number {
  return BUILDING_IDS.reduce((sum, id) => sum + buildingPoints(id, levels[id] ?? 0), 0);
}

// ---------- Zustimmung (Loyalität) ----------

export const LOYALTY = {
  max: 100,
  afterConquest: 25,
  regenPerHour: 1, // wird mit WORLD.speed multipliziert
  hitMin: 20, // Senkung pro erfolgreichem Fürsten-Angriff
  hitMax: 35,
};

export function loyaltyAt(stored: number, sinceMs: number, atMs: number): number {
  const hours = Math.max(0, atMs - sinceMs) / 3_600_000;
  return Math.min(LOYALTY.max, stored + hours * LOYALTY.regenPerHour * WORLD.speed);
}

export function formatDuration(totalSec: number): string {
  const s = Math.max(0, Math.ceil(totalSec));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return `${h}:${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
}
